import { Card, ErrorState, FactRow, Notice, PageHeader, SkeletonList, StatTile } from "@teamna/ui/components/index.ts";
import { useAdminHealth } from "../lib/cc-api.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";

/**
 * Is Teamna working right now (web-app-v2.md section 5).
 *
 * A tile for a failure count turns red when it is above zero, because on this page any failure is
 * a thing to look at. The queue depth tiles (created, active) stay neutral: work in the queue is
 * normal. The page asks again every 30 seconds while the window has focus.
 */
export function AdminHealthScreen() {
  const health = useAdminHealth();
  const data = health.data;

  const description = data ? `Database time ${formatDateTime(data.databaseNow)}` : undefined;

  if (!data) {
    return (
      <>
        <PageHeader title="Health" />
        {health.isLoading ? (
          <output aria-label="Loading health" className="block">
            <SkeletonList rows={2} />
          </output>
        ) : (
          <ErrorState error={health.error} onRetry={() => void health.refetch()} />
        )}
      </>
    );
  }

  const failure = (count: number): "bad" | "neutral" => (count > 0 ? "bad" : "neutral");
  const generator = data.sessionGenerator;

  return (
    <>
      <PageHeader title="Health" description={description} />

      {data.queue === null ? (
        <div className="mb-4">
          <Notice tone="warning" title="The job queue cannot be read">
            {data.queueUnavailableReason ?? "The API did not give a reason."}
          </Notice>
        </div>
      ) : null}

      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {data.queue ? (
          <>
            <StatTile label="Queue created" value={data.queue.created} />
            <StatTile label="Queue active" value={data.queue.active} />
            <StatTile
              label="Queue failed"
              value={data.queue.failed}
              tone={failure(data.queue.failed)}
            />
          </>
        ) : null}
        <StatTile
          label="Failed webhooks 24h"
          value={data.webhookEventsFailed24h}
          tone={failure(data.webhookEventsFailed24h)}
        />
        <StatTile
          label="Undelivered sign-in codes 24h"
          value={data.signInCodesUndelivered24h}
          tone={failure(data.signInCodesUndelivered24h)}
        />
      </div>

      <h2 className="mb-2.5 text-[17px] font-semibold">Session generator</h2>
      <Card className="max-w-[560px] px-4">
        <FactRow
          label="Last session created"
          value={
            generator.lastSessionCreatedAt
              ? formatDateTime(generator.lastSessionCreatedAt)
              : "No session yet"
          }
        />
        <FactRow
          label="Last job completed"
          value={
            generator.lastJobCompletedAt
              ? formatDateTime(generator.lastJobCompletedAt)
              : "not through the queue"
          }
          last
        />
      </Card>
    </>
  );
}
